import { useEffect, useId, useRef, useState } from 'react';
import { useUnitAlerts } from '../../hooks/useUnitAlerts';
import type { AlertConfig } from '../../hooks/useUnitAlerts';
import './AlertConfigDialog.css';

interface AlertConfigDialogProps {
  unitId: string;
  unitName?: string;
  metric: AlertConfig['metric'];
  metricLabel: string;
  totalVolume?: number;
  onClose: () => void;
}

export function AlertConfigDialog({ unitId, unitName, metric, metricLabel, totalVolume = 0, onClose }: AlertConfigDialogProps) {
  const { alerts, isLoading, error, addAlert, removeAlert } = useUnitAlerts(unitId, metric);
  const [condition, setCondition] = useState<'lt' | 'gt'>('lt');
  const [threshold, setThreshold] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const titleId = useId();
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const isFilledPercent = metricLabel === 'Filled Percentage' && totalVolume > 0;

  // Escape closes the dialog, and focus moves inside it on open — otherwise
  // keyboard users stay parked on the trigger button behind the backdrop.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    closeButtonRef.current?.focus();
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const handleAdd = async () => {
    if (!threshold) return;
    setIsSubmitting(true);
    try {
      let finalThreshold = parseFloat(threshold);
      if (isFilledPercent) {
        finalThreshold = (finalThreshold / 100) * totalVolume;
      }
      await addAlert({
        unitId,
        metric,
        condition,
        threshold: finalThreshold,
      });
      setThreshold('');
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getMetricUnit = (metricKey: AlertConfig['metric']) => {
    if (metricLabel === 'Filled Percentage') {
      return '%';
    }
    switch (metricKey) {
      case 'battery_capacity':
        return '%';
      case 'voltage':
        return 'V';
      case 'charge_rate':
        return 'V/hr';
      case 'water_depth':
        return 'cm';
      case 'water_volume':
      case 'volume_change':
        return 'L';
      default:
        return '';
    }
  };

  const conditionLabel = (cond: string) => cond === 'lt' ? '<' : '>';
  const unitSuffix = getMetricUnit(metric);

  return (
    <div className="alert-dialog-backdrop" onClick={onClose}>
      <div
        className="alert-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={e => e.stopPropagation()}
      >
        <div className="alert-dialog-header">
          <div className="alert-dialog-header-title-group">
            <h2 id={titleId}>Alerts: {metricLabel}</h2>
            {unitName && <div className="alert-dialog-unit-name">Unit: {unitName}</div>}
          </div>
          <button
            type="button"
            ref={closeButtonRef}
            className="close-btn"
            aria-label="Close alerts dialog"
            onClick={onClose}
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        
        <div className="alert-dialog-body">
          {isLoading ? (
            <p>Loading alerts...</p>
          ) : error ? (
            <p className="error-text">{error}</p>
          ) : (
            <div className="alert-list">
              {alerts.length === 0 ? (
                <p className="empty-text">No alerts configured for this metric.</p>
              ) : (
                alerts.map(alert => {
                  const id = alert.alertId || alert.id;
                  let displayThreshold = alert.threshold;
                  if (isFilledPercent) {
                    displayThreshold = Math.round((alert.threshold / totalVolume) * 100);
                  }
                  return (
                    <div key={id} className="alert-item">
                      <span>{metricLabel} {conditionLabel(alert.condition)} {displayThreshold} {unitSuffix}</span>
                      <button type="button" className="btn-delete" onClick={() => id && removeAlert(id)}>Remove</button>
                    </div>
                  );
                })
              )}
            </div>
          )}

          <div className="alert-add-form">
            <h3>Add New Alert</h3>
            <div className="form-group-row">
              <select value={condition} onChange={(e) => setCondition(e.target.value as any)}>
                <option value="lt">Less Than (&lt;)</option>
                <option value="gt">Greater Than (&gt;)</option>
              </select>
              <div className="input-with-unit">
                <input 
                  type="number" 
                  placeholder="Threshold" 
                  value={threshold} 
                  onChange={(e) => setThreshold(e.target.value)} 
                />
                <span className="input-unit">{unitSuffix}</span>
              </div>
              <button
                type="button"
                className="btn-add"
                onClick={handleAdd}
                disabled={isSubmitting || !threshold}
              >
                {isSubmitting ? 'Adding...' : 'Add'}
              </button>
            </div>
            <p className="alert-dialog-help-text">
              Alert will be triggered every time the threshold is reached.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
